import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { confirmGoal, getGoals, qk, rejectGoal, type Goal } from '../api.js';
import BilgiDugmesi from '../components/BilgiDugmesi.js';
import Badge from '../components/Badge.js';
import Card from '../components/Card.js';
import EmptyState, { ErrorState } from '../components/EmptyState.js';
import { LoadingBlock } from '../components/Spinner.js';
import { useActivePid } from '../components/Layout.js';
import { formatNumber } from '../lib/format.js';

/**
 * §13.4 Hedefler — keşfedilen goal adayları + confidence + kanıt bileşenleri;
 * Onayla / Reddet. En yüksek skorlu `proposed` aday "Hedefiniz bu mu?" kartıyla sunulur (§11.5).
 *
 * Görsel dil referans DXP admin panelindeki sayfa kabuğuyla birebir:
 * beyaz başlık şeridi → beyaz istatistik şeridi → #fafafa zeminde px-6 py-8 içerik.
 */

const COMPONENTS: { key: 'semantic' | 'narrowing' | 'terminality' | 'commitment'; label: string; hint: string }[] = [
  { key: 'semantic', label: 'Anlamsal', hint: 'URL kelimeleri (onay/tesekkur → 1.0)' },
  { key: 'narrowing', label: 'Daralma', hint: 'Huni sonuna doğru trafiğin daralması' },
  { key: 'terminality', label: 'Sonluluk', hint: 'Son 2 sayfada ve sonrasında sürtünme yok' },
  { key: 'commitment', label: 'Taahhüt', hint: 'Öncesinde form gönderimi / ödeme çağrısı' },
];

/** §11.5 kanıt bileşeni — 0..1 skorun mini barı. */
function EvidenceBar({ label, hint, value }: { label: string; hint: string; value: number }) {
  const pct = Math.max(0, Math.min(100, value * 100));
  return (
    <div title={hint}>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[12px] text-[#9ca3af]">{label}</span>
        <span className="num text-[12px] font-semibold text-[#374151]">%{pct.toFixed(0)}</span>
      </div>
      <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded bg-[#f3f4f6]">
        <div className="h-full rounded bg-brand" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

/** Hedef kartlarının ortak kabuğu — sol kenarı marka rengiyle vurgulanmış beyaz yüzey. */
function GoalHighlightCard({
  eyebrow,
  goal,
  detail,
  badge,
  actions,
}: {
  eyebrow: string;
  goal: Goal;
  detail: ReactNode;
  badge?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <section className="rounded-2xl border border-[#e5e7eb] border-l-4 border-l-brand bg-white px-5 py-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-[13px] font-semibold text-brand">{eyebrow}</p>
            {badge}
          </div>
          <p className="mt-1.5 truncate font-mono text-[17px] text-[#111827]">{goal.url_pattern}</p>
          <p className="mt-1.5 text-[13px] text-[#6b7280]">{detail}</p>
        </div>
        {actions !== undefined ? (
          <div className="flex shrink-0 items-center gap-2">{actions}</div>
        ) : null}
      </div>
    </section>
  );
}

export default function Goals() {
  const pid = useActivePid();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: qk.goals(pid),
    queryFn: ({ signal }) => getGoals(pid, signal),
  });

  const mutation = useMutation({
    mutationFn: ({ id, action }: { id: number; action: 'confirm' | 'reject' }) =>
      action === 'confirm' ? confirmGoal(id) : rejectGoal(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['goals'] });
      void queryClient.invalidateQueries({ queryKey: ['funnel'] });
    },
  });

  const goals = [...(query.data ?? [])].sort((a, b) => b.confidence - a.confidence);
  const confirmed = goals.filter((g) => g.status === 'confirmed');
  const proposed = goals.filter((g) => g.status === 'proposed');
  const rejected = goals.filter((g) => g.status === 'rejected');
  const top = proposed[0];

  const ready = !query.isLoading && !query.isError;
  const activeGoal = confirmed[0];
  const bestConfidence = goals[0]?.confidence;
  // Aday listesi: önce onay bekleyenler, sonra reddedilenler (arşiv olarak görünür kalır).
  const candidates = [...proposed, ...rejected];

  return (
    <div className="min-h-full bg-[#fafafa]" data-testid="hedefler-sayfasi">
      {/* başlık */}
      <div className="flex items-center justify-between border-b border-[#e5e7eb] bg-white px-6 py-4">
        <div className="flex items-center gap-2">
          <h1 className="font-grotesk text-[18px] font-bold text-[#111827]">Hedefler</h1>
          <BilgiDugmesi testId="hedefler-bilgi">Hedef otomatik keşfedilir; siz hiçbir şey tanımlamazsınız (§11.5). Güven skoru dört kanıt bileşeninden hesaplanır.</BilgiDugmesi>
          <span className="ml-1 hidden text-[13px] text-[#9ca3af] md:inline">
            Otomatik keşfedilen hedef adayları
          </span>
        </div>
      </div>

      {/* istatistikler */}
      <div className="flex flex-wrap items-center gap-8 border-b border-[#e5e7eb] bg-white px-6 py-2.5">
        {query.isLoading ? (
          <span className="animate-pulse text-[12px] text-[#9ca3af]">Veri yükleniyor…</span>
        ) : (
          [
            { label: 'Onaylı', value: formatNumber(confirmed.length) },
            { label: 'Aday', value: formatNumber(proposed.length) },
            { label: 'Reddedilen', value: formatNumber(rejected.length) },
            {
              label: 'En yüksek güven',
              value: bestConfidence === undefined ? '—' : `%${(bestConfidence * 100).toFixed(1)}`,
            },
          ].map((s) => (
            <div key={s.label} className="flex items-center gap-2">
              <span className="text-[12px] text-[#9ca3af]">{s.label}</span>
              <span className="num text-[13px] font-semibold text-[#111827]">{s.value}</span>
            </div>
          ))
        )}
      </div>

      {/* içerik */}
      <div className="space-y-6 px-6 py-8">
        {query.isLoading ? <LoadingBlock label="Hedefler yükleniyor…" /> : null}

        {query.isError ? (
          <ErrorState
            title="Hedefler alınamadı"
            description={(query.error as Error).message}
            onRetry={() => void query.refetch()}
          />
        ) : null}

        {ready && goals.length === 0 ? (
          <EmptyState
            title="Hedef henüz keşfedilmedi"
            description="Sistem hedefi otomatik bulur — son 14 günde en az 20 oturumda görülen sayfalar aday olur. Trafik biriktikçe adaylar burada listelenir."
          />
        ) : null}

        {ready && goals.length > 0 ? (
          <>
            {activeGoal ? (
              /* Onaylanmış hedef — dönüşüm ve gelir hesapları buna bağlıdır. */
              <GoalHighlightCard
                eyebrow="Aktif hedef"
                goal={activeGoal}
                badge={<Badge tone="success">onaylandı</Badge>}
                detail={
                  <>
                    Güven %{(activeGoal.confidence * 100).toFixed(1)} · {activeGoal.kind} · dönüşüm
                    ve gelir hesapları bu hedefe bağlıdır.
                  </>
                }
              />
            ) : top ? (
              /* §11.5 sonu — onay yoksa en yüksek proposed geçici olarak kullanılır. */
              <GoalHighlightCard
                eyebrow="Hedefiniz bu mu?"
                goal={top}
                detail={
                  <>
                    Güven %{(top.confidence * 100).toFixed(1)} — onaylanana kadar bu hedef{' '}
                    <em>otomatik tespit</em> olarak kullanılıyor.
                  </>
                }
                actions={
                  <>
                    <button
                      type="button"
                      className="btn-primary"
                      disabled={mutation.isPending}
                      onClick={() => mutation.mutate({ id: top.id, action: 'confirm' })}
                    >
                      Onayla
                    </button>
                    <button
                      type="button"
                      className="btn-ghost"
                      disabled={mutation.isPending}
                      onClick={() => mutation.mutate({ id: top.id, action: 'reject' })}
                    >
                      Reddet
                    </button>
                  </>
                }
              />
            ) : null}

            <Card title="Aday hedefler" subtitle="Skor bileşenleri §11.5 formülünden gelir">
              {/* Tüm adaylar onaylandıysa liste boş kalır — kart içinde sıkışık boş durum. */}
              {candidates.length === 0 ? (
                <EmptyState
                  compact
                  title="Bekleyen aday yok"
                  description="Keşfedilen tüm hedefler karara bağlandı. Yeni bir desen öne çıkarsa burada belirir."
                />
              ) : null}

              <ul className="space-y-3">
                {candidates.map((goal) => (
                  <li key={goal.id} className="rounded-xl border border-[#e5e7eb] p-4">
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="truncate font-mono text-[13px] text-[#111827]">
                            {goal.url_pattern}
                          </span>
                          {goal.status === 'proposed' ? (
                            <Badge tone="accent" size="sm">
                              otomatik tespit
                            </Badge>
                          ) : (
                            <Badge tone="neutral" size="sm">
                              reddedildi
                            </Badge>
                          )}
                        </div>
                        <p className="mt-1 text-[12px] text-[#9ca3af]">
                          {formatNumber(goal.evidence?.candidateSessions ?? 0)} /{' '}
                          {formatNumber(goal.evidence?.totalSessions ?? 0)} oturumda görüldü ·{' '}
                          {goal.kind}
                        </p>
                      </div>

                      <div className="flex shrink-0 items-center gap-4">
                        <div className="text-right">
                          <p className="text-[12px] text-[#9ca3af]">güven</p>
                          <p className="num text-[20px] font-bold text-[#111827]">
                            %{(goal.confidence * 100).toFixed(1)}
                          </p>
                        </div>
                        {goal.status === 'proposed' ? (
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              className="btn-primary"
                              disabled={mutation.isPending}
                              onClick={() => mutation.mutate({ id: goal.id, action: 'confirm' })}
                            >
                              Onayla
                            </button>
                            <button
                              type="button"
                              className="btn-ghost"
                              disabled={mutation.isPending}
                              onClick={() => mutation.mutate({ id: goal.id, action: 'reject' })}
                            >
                              Reddet
                            </button>
                          </div>
                        ) : null}
                      </div>
                    </div>

                    {/* §11.5 kanıt bileşenleri — dördü ayrı mini bar */}
                    <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                      {COMPONENTS.map(({ key, label, hint }) => (
                        <EvidenceBar
                          key={key}
                          label={label}
                          hint={hint}
                          value={Number(goal.evidence?.[key] ?? 0)}
                        />
                      ))}
                    </div>
                  </li>
                ))}
              </ul>
            </Card>
          </>
        ) : null}
      </div>
    </div>
  );
}
